import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Chip, Typography, alpha } from '@mui/material';
import {
  BannerElementLayout,
  BannerElementStyles,
  ElementPosition,
  ElementStyle,
} from '../types/BannerAdmin';

export type BannerPreviewData = {
  title?: string;
  subtitle?: string;
  tag?: string;
  tagColor?: string;
  ctaText?: string;
  ctaUrl?: string;
  imageUrl?: string;
  bgColor?: string;
  textColor?: string;
};

export type BannerElementKey = 'tag' | 'title' | 'subtitle' | 'cta';

export const BANNER_DESIGN_WIDTH = 600;
export const BANNER_DESIGN_HEIGHT = 380;
export const BANNER_CORNER_RADIUS = 3;

export const DEFAULT_LAYOUT: Required<BannerElementLayout> = {
  tag:      { x: 5, y: 10 },
  title:    { x: 5, y: 25 },
  subtitle: { x: 5, y: 55 },
  cta:      { x: 5, y: 75 },
};

export const DEFAULT_STYLES: Required<BannerElementStyles> = {
  tag:      { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, maxWidth: 40 },
  title:    { fontSize: 28, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0, maxWidth: 70 },
  subtitle: { fontSize: 14, fontWeight: 400, textTransform: 'none', letterSpacing: 0, maxWidth: 60 },
  cta:      { fontSize: 13, fontWeight: 600, textTransform: 'none', letterSpacing: 0, maxWidth: 30 },
};

export const resolveStyle = (key: BannerElementKey, styles?: BannerElementStyles): Required<ElementStyle> => {
  const base = DEFAULT_STYLES[key];
  const user = styles?.[key];
  return {
    fontSize: user?.fontSize ?? base.fontSize ?? 14,
    fontWeight: user?.fontWeight ?? base.fontWeight ?? 400,
    fontFamily: user?.fontFamily ?? '',
    letterSpacing: user?.letterSpacing ?? base.letterSpacing ?? 0,
    textTransform: user?.textTransform ?? base.textTransform ?? 'none',
    fontStyle: user?.fontStyle ?? 'normal',
    color: user?.color ?? '',
    backgroundColor: user?.backgroundColor ?? '',
    maxWidth: user?.maxWidth ?? base.maxWidth ?? 100,
    padding: user?.padding ?? 0,
    margin: user?.margin ?? 0,
    height: user?.height ?? 0,
    width: user?.width ?? 0,
  };
};

type Props = {
  data: BannerPreviewData;
  layout?: BannerElementLayout;
  elementStyles?: BannerElementStyles;
  designWidth?: number;
  designHeight?: number;
  onClick?: () => void;
  allowUpscale?: boolean;
};

const DEFAULT_DESIGN_WIDTH = BANNER_DESIGN_WIDTH;
const DEFAULT_DESIGN_HEIGHT = BANNER_DESIGN_HEIGHT;

const BannerPreview: FC<Props> = ({
  data,
  layout,
  elementStyles,
  designWidth = DEFAULT_DESIGN_WIDTH,
  designHeight = DEFAULT_DESIGN_HEIGHT,
  onClick,
  allowUpscale = false,
}) => {
  const color = data.textColor || '#fff';
  const pos = (key: BannerElementKey): ElementPosition =>
    layout?.[key] ?? DEFAULT_LAYOUT[key];
  const style = (key: BannerElementKey) => resolveStyle(key, elementStyles);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0);

  const measure = useCallback(() => {
    if (!wrapperRef.current) return;
    const w = wrapperRef.current.clientWidth;
    const nextScale = w / designWidth;
    setScale(allowUpscale ? nextScale : Math.min(1, nextScale));
  }, [allowUpscale, designWidth]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const ro = new ResizeObserver(measure);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [measure]);

  const textSx = (key: BannerElementKey, fallbackColor: string) => {
    const s = style(key);
    return {
      fontSize: `${s.fontSize}px`,
      fontWeight: s.fontWeight,
      ...(s.fontFamily ? { fontFamily: s.fontFamily } : {}),
      letterSpacing: s.letterSpacing ? `${s.letterSpacing}px` : undefined,
      textTransform: s.textTransform,
      fontStyle: s.fontStyle,
      color: s.color || fallbackColor,
      lineHeight: 1.2,
      ...(s.padding ? { padding: `${s.padding}px` } : {}),
      ...(s.margin ? { margin: `${s.margin}px` } : {}),
      ...(s.height ? { height: `${s.height}px` } : {}),
      ...(s.width ? { width: `${s.width}px` } : {}),
    };
  };

  return (
    <Box
      ref={wrapperRef}
      sx={{ width: '100%', height: designHeight * scale, overflow: 'hidden' }}
    >
      <Box
        sx={{
          width: designWidth,
          height: designHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'relative',
          borderRadius: `${BANNER_CORNER_RADIUS}px`,
          overflow: 'hidden',
          backgroundColor: data.bgColor || '#1A1A2E',
          backgroundImage: data.imageUrl ? `url(${data.imageUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color,
          cursor: onClick ? 'pointer' : 'default',
        }}
        onClick={onClick}
      >
        {data.imageUrl && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.10) 100%)',
              pointerEvents: 'none',
            }}
          />
        )}

        {data.tag && (
          <Box sx={{ position: 'absolute', left: `${pos('tag').x}%`, top: `${pos('tag').y}%`, zIndex: 1, maxWidth: `${style('tag').maxWidth}%`, ...(style('tag').margin ? { margin: `${style('tag').margin}px` } : {}) }}>
            <Chip
              label={data.tag}
              size="small"
              sx={{
                background: `linear-gradient(90deg, ${style('tag').backgroundColor || data.tagColor || '#16A34A'} 0%, ${alpha(style('tag').backgroundColor || data.tagColor || '#16A34A', 0)} 100%)`,
                color: style('tag').color || '#fff',
                fontWeight: style('tag').fontWeight,
                fontSize: `${style('tag').fontSize}px`,
                ...(style('tag').fontFamily ? { fontFamily: style('tag').fontFamily } : {}),
                letterSpacing: style('tag').letterSpacing ? `${style('tag').letterSpacing}px` : '0.04em',
                textTransform: style('tag').textTransform,
                height: style('tag').height || Math.max(22, style('tag').fontSize + 12),
                ...(style('tag').width ? { width: `${style('tag').width}px` } : {}),
                ...(style('tag').padding ? { padding: `${style('tag').padding}px` } : {}),
                borderRadius: '6px',
              }}
            />
          </Box>
        )}

        {data.title && (
          <Box sx={{ position: 'absolute', left: `${pos('title').x}%`, top: `${pos('title').y}%`, zIndex: 1, maxWidth: `${style('title').maxWidth}%` }}>
            <Typography
              component="div"
              sx={textSx('title', color)}
            >
              {data.title}
            </Typography>
          </Box>
        )}

        {data.subtitle && (
          <Box sx={{ position: 'absolute', left: `${pos('subtitle').x}%`, top: `${pos('subtitle').y}%`, zIndex: 1, maxWidth: `${style('subtitle').maxWidth}%` }}>
            <Typography
              component="div"
              sx={textSx('subtitle', alpha(color === '#fff' || color === '#FFFFFF' ? '#fff' : color, 0.85))}
            >
              {data.subtitle}
            </Typography>
          </Box>
        )}

        {data.ctaText && (
          <Box sx={{ position: 'absolute', left: `${pos('cta').x}%`, top: `${pos('cta').y}%`, zIndex: 1, maxWidth: `${style('cta').maxWidth}%`, ...(style('cta').margin ? { margin: `${style('cta').margin}px` } : {}) }}>
            <Chip
              label={data.ctaText}
              sx={{
                backgroundColor: style('cta').backgroundColor || '#fff',
                color: style('cta').color || '#1A1A2E',
                fontWeight: style('cta').fontWeight,
                fontSize: `${style('cta').fontSize}px`,
                ...(style('cta').fontFamily ? { fontFamily: style('cta').fontFamily } : {}),
                ...(style('cta').letterSpacing ? { letterSpacing: `${style('cta').letterSpacing}px` } : { letterSpacing: '0.02em' }),
                textTransform: style('cta').textTransform,
                fontStyle: style('cta').fontStyle,
                height: style('cta').height || Math.max(32, style('cta').fontSize + 18),
                ...(style('cta').width ? { width: `${style('cta').width}px` } : {}),
                ...(style('cta').padding ? { padding: `${style('cta').padding}px` } : {}),
                borderRadius: '5px',
                px: 1.5,
                '& .MuiChip-label': {
                  px: 1.5,
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BannerPreview;